import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Trust.scss";

import trustIcon from "../img/trust.png";
import { getCurrentUserId, getAccessToken } from "../../../awsAuth";
import DynamicHeader from "../../Header/DynamicHeader";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const API = API_BASE;

const STATE_LABELS = {
  good: "Добре",
  neutral: "Нормально",
  weak: "Слабко",
  bad: "Проблема",
  none: "Немає даних",
};

const PERIODS = [
  { key: "30d", label: "30 днів", days: 30 },
  { key: "90d", label: "90 днів", days: 90 },
  { key: "all", label: "Весь час", days: null },
];

function useFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!url);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!url) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getAccessToken().catch(() => null);
        const r = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return { data, loading, error, setData };
}

const Empty = ({ title, subtitle }) => (
  <div className="empty">
    <div className="empty__title">{title}</div>
    {subtitle && <div className="empty__subtitle">{subtitle}</div>}
  </div>
);

const STATUS_ACCENT = {
  established: "accent--good",
  developing: "accent--neutral",
  new: "accent--neutral",
  under_review: "accent--bad",
};

const Trust = () => {
  const navigate = useNavigate();
  const userId = getCurrentUserId?.();

  useEffect(() => {
    if (!userId) navigate("/auth");
  }, [userId, navigate]);

  const detailsUrl = userId ? `${API}/api/trust/${userId}/details` : null;
  const { data: details, loading, error, setData: setDetails } = useFetch(detailsUrl);

  // Live updates via SSE, with a polling fallback — same transport as
  // before, just fed into the new payload shape directly.
  useEffect(() => {
    if (!userId) return;
    let es;
    let pollTimer;

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const startPolling = () => {
      stopPolling();
      pollTimer = setInterval(async () => {
        try {
          const token = await getAccessToken().catch(() => null);
          const d = await fetch(`${API}/api/trust/${userId}/details`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then((r) => r.json());
          setDetails(d);
        } catch {
          /* ignore */
        }
      }, 20000);
    };

    (async () => {
      try {
        const token = await getAccessToken().catch(() => null);
        es = new EventSource(
          `${API}/api/trust/${userId}/stream?token=${encodeURIComponent(token || "")}`
        );
        es.onmessage = (e) => {
          if (!e?.data) return;
          const payload = JSON.parse(e.data);
          if (payload?.details) setDetails({ userId, ...payload.details });
        };
        es.onerror = () => {
          try {
            es.close();
          } catch {}
          startPolling();
        };
      } catch {
        startPolling();
      }
    })();

    return () => {
      try {
        es?.close();
      } catch {}
      stopPolling();
    };
  }, [userId, setDetails]);

  const accentClass = details ? STATUS_ACCENT[details.status] || "accent--neutral" : "";

  return (
    <>
      <DynamicHeader
        title="Рейтинг та довіра"
        variant="glass"
        size="regular"
        showBack
        withSpacer
      />

      <div className="trust">
        <div className={`trust__summary ${accentClass}`}>
          <div className="trust__summary-icon">
            <img src={trustIcon} alt="" />
          </div>

          {loading ? (
            <div className="loading">Завантаження…</div>
          ) : error ? (
            <Empty title="Не вдалося завантажити рейтинг" subtitle="Спробуйте пізніше." />
          ) : details ? (
            <SummaryBlock details={details} />
          ) : null}
        </div>

        {details && <HistoryCard history={details.history} />}
        {details && <FactorsCard factors={details.factors} />}
        {details && details.insights?.length > 0 && <InsightsCard insights={details.insights} />}
      </div>
    </>
  );
};

const SummaryBlock = ({ details }) => {
  if (!details.displayScore) {
    return (
      <div className="trust__summary-body">
        <div className="trust__forming-badge">Рейтинг формується</div>
        <div className="trust__status-message">
          {details.statusMessage || "Cvanto ще не має достатньо даних для оцінки надійності."}
        </div>
      </div>
    );
  }

  return (
    <div className="trust__summary-body">
      <div className="trust__score">
        <span className="trust__score-value">{details.score.toFixed(1)}</span>
        <span className="trust__score-of">/10</span>
      </div>
      <div className="trust__label">{details.label}</div>
      {details.evidenceSummary && (
        <div className="trust__evidence">{details.evidenceSummary}</div>
      )}
    </div>
  );
};

const HistoryCard = ({ history }) => {
  const items = Array.isArray(history) ? history : [];
  const [period, setPeriod] = useState("30d");

  const filtered = useMemo(() => {
    const def = PERIODS.find((p) => p.key === period);
    if (!def || def.days == null) return items;
    const cutoff = Date.now() - def.days * 24 * 60 * 60 * 1000;
    return items.filter((x) => new Date(x.at).getTime() >= cutoff);
  }, [items, period]);

  return (
    <div className="trust__card">
      <div className="card__head">
        <div className="card__title">Динаміка довіри</div>
        {items.length > 1 && (
          <div className="period-switch">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={period === p.key ? "active" : ""}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <Empty title="Історії ще немає" subtitle="Графік з'явиться, коли з'являться перші дані." />
      ) : filtered.length < 2 ? (
        <Empty
          title="Замало даних за цей період"
          subtitle="Спробуйте обрати довший період."
        />
      ) : (
        <TrustChart points={filtered} />
      )}
    </div>
  );
};

const TrustChart = ({ points }) => {
  const pts = [...points]
    .map((p) => ({ x: new Date(p.at).getTime(), y: Math.max(0, Math.min(10, Number(p.score) || 0)) }))
    .sort((a, b) => a.x - b.x);

  const minX = pts[0].x;
  const maxX = pts[pts.length - 1].x;
  const spanX = Math.max(1, maxX - minX);
  const w = 720;
  const h = 220;
  const pad = 12;

  const toXY = (p) => ({
    x: pad + ((p.x - minX) / spanX) * (w - pad * 2),
    y: pad + (1 - p.y / 10) * (h - pad * 2),
  });
  const coords = pts.map(toXY);

  let pathD = "";
  coords.forEach((p, i) => {
    pathD += i === 0 ? `M ${p.x.toFixed(1)},${p.y.toFixed(1)}` : ` L ${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  });

  const first = coords[0];
  const last = coords[coords.length - 1];
  const yBase = h - pad;
  const areaD = `${pathD} L ${last.x.toFixed(1)},${yBase} L ${first.x.toFixed(1)},${yBase} Z`;

  return (
    <div className="trust-chart">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="trustArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--trust-accent)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--trust-accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaD} className="trust-chart__area" />
        <path d={pathD} className="trust-chart__line" />
        <circle cx={last.x} cy={last.y} r="4" className="trust-chart__dot" />
      </svg>
    </div>
  );
};

const FactorsCard = ({ factors }) => {
  if (!factors?.length) return null;
  return (
    <div className="trust__card">
      <div className="card__head">
        <div className="card__title">Що впливає на рейтинг</div>
      </div>
      <div className="factors">
        {factors.map((f) => (
          <div key={f.key} className={`factor factor--${f.state}`}>
            <div className="factor__row">
              <span className="factor__label">{f.label}</span>
              <span className="factor__pill">{STATE_LABELS[f.state] || f.state}</span>
            </div>
            <div className="factor__metric">{f.metric}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const InsightsCard = ({ insights }) => (
  <div className="trust__card">
    <div className="card__head">
      <div className="card__title">Що покращити</div>
    </div>
    <ul className="insights">
      {insights.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  </div>
);

export default Trust;
